import { Component, Inject, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { ButtonStyle } from '@constants/button/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  LoadedImage,
  ImageCropperModule,
} from 'ngx-image-cropper';
import { take } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NumbersOnlyDirective } from '../../../directives/numbers-only/1.0/numbers-only.directive';
import { ButtonComponent } from '../../button/1.0/button.component';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { TranslateModule } from '@ngx-translate/core';

class ImageBlobEvent extends Event {
  constructor(public blob: Blob) {
    super('imageBlobEvent');
  }
}

@Component({
  selector: 'txp-crop-image-dialog',
  templateUrl: './crop-image-dialog.component.html',
  styleUrls: ['./crop-image-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NumbersOnlyDirective,
    MatError,
    ButtonComponent,
    ImageCropperModule,
    MatDialogActions,
    LoadingComponent,
    TranslateModule,
  ],
})
export class CropImageDialogComponent {
  buttonStyle = ButtonStyle;
  loadingColor = LoadingColor;
  croppedImage: ImageCroppedEvent;
  isLoadingImage = true;
  hasDimensionsChanged = false;
  initialWidth: number;
  initialHeight: number;
  imageChangedEvent: Event | null = null;
  form: UntypedFormGroup;

  @ViewChild(ImageCropperComponent) cropper: ImageCropperComponent;
  constructor(
    private dialogRef: MatDialogRef<CropImageDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private utilitiesService: UtilitiesService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.form = this.formBuilder.group({
      width: new UntypedFormControl(0, [
        Validators.required,
        Validators.min(0),
        Validators.max(99999),
      ]),
      height: new UntypedFormControl(0, [
        Validators.required,
        Validators.min(0),
        Validators.max(99999),
      ]),
    });
  }

  imageCropped(event: ImageCroppedEvent) {
    if (!this.croppedImage) {
      // set the initial dimensions
      this.initialWidth = event.width;
      this.initialHeight = event.height;
    }
    this.croppedImage = event;
    this.verifyImageDimensions(event.width, event.height);
    this.form.get('width').setValue(event.width, { emitEvent: false });
    this.form.get('height').setValue(event.height, { emitEvent: false });
  }

  imageLoaded(_image: LoadedImage) {
    this.isLoadingImage = false;
  }

  onAction(value: boolean) {
    if (value) {
      this.dialogRef.close({ value, croppedImage: this.croppedImage });
    } else {
      return this.dialogRef.close(value);
    }
  }

  resizeFileImage() {
    this.isLoadingImage = true;
    this.utilitiesService
      .resizeImage(this.data.imageFile, this.form.get('width').value, this.form.get('height').value)
      .pipe(take(1))
      .subscribe((resizedImage) => {
        this.imageChangedEvent = new ImageBlobEvent(resizedImage);
        this.data.imageFile = resizedImage;
        this.hasDimensionsChanged = true;
        this.isLoadingImage = false;
      });
  }

  verifyImageDimensions(width: number, height: number) {
    if (this.initialWidth !== width || this.initialHeight !== height) {
      this.hasDimensionsChanged = true;
    }
  }
}
